import { z } from 'zod'
import { ComponentCategory } from '../enums'
import { Id, Timestamp } from './common'

/** Sealant older than this since opening is worth checking or replacing. */
export const SEALANT_OLD_AFTER_DAYS = 180

/** A consumable on the shelf (pads, chains, tubes, sealant…), counted. */
export const StockItem = z.object({
  id: Id,
  category: ComponentCategory,
  brand: z.string().nullable(),
  model: z.string().nullable(),
  /** The rider's own words for it. Where set, it is the item's name. */
  description: z.string().nullable(),
  qty: z.number().int(),
  min_qty: z.number().int(),
  opened_at: z.string().nullable(),
  notes: z.string().nullable(),
  /** Below the minimum the rider wants on the shelf. */
  low: z.boolean(),
  /** Days since opened (sealant and other perishables), or null. */
  opened_days: z.number().int().nullable(),
  old: z.boolean(),
  updated_at: Timestamp,
})
export type StockItem = z.infer<typeof StockItem>

export const StockList = z.object({ items: z.array(StockItem) })

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional()

const count = z.number().int().min(0).max(9999)

/** The editable fields, without defaults: an update carries only what the rider changed. */
const StockFields = {
  category: ComponentCategory,
  brand: text(80),
  model: text(120),
  description: text(160),
  qty: count,
  min_qty: count,
  opened_at: z.iso.date().nullable().optional(),
  notes: text(500),
}

export const CreateStockBody = z.object({
  ...StockFields,
  qty: count.default(1),
  min_qty: count.default(0),
})
export type CreateStockBody = z.input<typeof CreateStockBody>

// Built from the fields, not from CreateStockBody.partial(): partial() keeps the
// defaults, so an update without qty would reset the count to 1.
export const UpdateStockBody = z
  .object(StockFields)
  .partial()
  .refine((b) => Object.keys(b).length > 0, 'Nothing to update.')
export type UpdateStockBody = z.input<typeof UpdateStockBody>

export const AdjustStockBody = z.object({ delta: z.number().int().min(-9999).max(9999) })

export const ShoppingItem = z.object({
  kind: z.enum(['restock', 'job']),
  label: z.string(),
  qty: z.number().int(),
  stock_id: Id.nullable(),
  job_id: Id.nullable(),
  job_title: z.string().nullable(),
})
export type ShoppingItem = z.infer<typeof ShoppingItem>

export const ShoppingList = z.object({ items: z.array(ShoppingItem) })
